import React, {Fragment, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {Box, Flex, SkeletonText, Text, useSlotRecipe, VisuallyHidden} from '@chakra-ui/react3'
import {useCurrency} from '../../../hooks/use-currency'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'
import displayPriceRecipe from './index.recipe'

/**
 * The DisplayPrice is a visual representation of all price settings of products
 * @param {'xpdp-configurator'} variant - the variant of the product tile:
 * - `xpdp-configurator`: price display in xpdp configurator (first line: "price incl. vat", second line: <price> <original price strikethrough>)
 *
 */
const DisplayPrice = ({
    price,
    displayPrice,
    currency,
    loading,
    isFree,
    hasDiscount,
    discountPercentage,
    variant,
    alignment
}) => {
    const {currency: activeCurrency} = useCurrency()
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    const recipe = useSlotRecipe({recipe: displayPriceRecipe})
    const styles = useMemo(
        () =>
            recipe({
                variant,
                alignment,
                isFree,
                hasDiscount,
                isCustomNetPriceEnabled
            }),
        [recipe, variant, alignment, isFree, hasDiscount, isCustomNetPriceEnabled]
    )

    const SkeletonComponent = loading ? SkeletonText : Fragment
    const skeletonProps = loading ? {noOfLines: 1, loading, as: 'div'} : {}

    return (
        <Box css={styles.container}>
            {!loading && (
                <Text css={styles.taxesLabel} data-testid="taxes">
                    {!isCustomNetPriceEnabled ? (
                        <>
                            <FormattedMessage
                                id="global.price_incl_vat"
                                defaultMessage="Price (incl. VAT)"
                            />
                        </>
                    ) : (
                        <FormattedMessage
                            id="global.price_excl_vat"
                            defaultMessage="Price (excl. VAT)"
                        />
                    )}
                </Text>
            )}
            <Flex css={styles.priceOrder}>
                {hasDiscount && (
                    <SkeletonComponent {...skeletonProps}>
                        <Text css={styles.originalPrice} data-testid="price">
                            <VisuallyHidden>
                                <FormattedMessage
                                    defaultMessage="Original price: "
                                    id="item_attributes.label.original_price"
                                />
                            </VisuallyHidden>
                            <FormattedNumber
                                style="currency"
                                currency={currency || activeCurrency}
                                value={price}
                            />
                        </Text>
                    </SkeletonComponent>
                )}
                <SkeletonComponent {...skeletonProps}>
                    <Box css={styles.priceContainer}>
                        {hasDiscount && (
                            <VisuallyHidden>
                                <FormattedMessage
                                    defaultMessage="Discounted price: "
                                    id="item_attributes.label.discounted_price"
                                />
                            </VisuallyHidden>
                        )}
                        {isFree ? (
                            <Text css={styles.freeLabel} data-testid="isFree">
                                <FormattedMessage defaultMessage="Free" id="global.free" />
                            </Text>
                        ) : (
                            <Text css={styles.price} data-testid="displayPrice">
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || activeCurrency}
                                    value={displayPrice}
                                />
                            </Text>
                        )}
                    </Box>
                </SkeletonComponent>
            </Flex>
            {hasDiscount && (
                <Box css={styles.discountLabel} data-testid="discountText">
                    <FormattedMessage
                        defaultMessage="You save {discountPercentage}%"
                        values={{discountPercentage: discountPercentage}}
                        id="item_attributes.label.savings"
                    />
                </Box>
            )}
        </Box>
    )
}

DisplayPrice.propTypes = {
    price: PropTypes.number,
    displayPrice: PropTypes.number,
    currency: PropTypes.string,
    loading: PropTypes.bool,
    isFree: PropTypes.bool,
    hasDiscount: PropTypes.bool,
    discountPercentage: PropTypes.number,
    variant: PropTypes.string,
    alignment: PropTypes.oneOf(['start', 'center', 'end'])
}

export default DisplayPrice
